import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Mail, X, Bell, Clock, FlaskConical } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DatePicker } from '../components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import api from '../services/api';
import { toast } from 'sonner';

export const NewReportPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromSensoryId = searchParams.get('from_sensory');
  
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [baseFields, setBaseFields] = useState([]);
  const [sourceSensoryTest, setSourceSensoryTest] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    batch: '',
    quantity: '',
    category_id: 'none',
    status_id: '',
    custom_fields: {},
    notification_emails: [],
    dmt_notification: null,
    source_sensory_test_id: null
  });
  const [emailInput, setEmailInput] = useState('');
  
  // DMT Notification Modal state
  const [dmtModalOpen, setDmtModalOpen] = useState(false);
  const [dmtDaysBefore, setDmtDaysBefore] = useState('7');
  const [dmtEmails, setDmtEmails] = useState([]);
  const [dmtEmailInput, setDmtEmailInput] = useState('');

  useEffect(() => {
    fetchFormConfig();
  }, []);

  // Load sensory test data if creating from one
  useEffect(() => {
    if (fromSensoryId) {
      fetchSensoryTestData();
    }
  }, [fromSensoryId]);

  const fetchSensoryTestData = async () => {
    try {
      const response = await api.get(`/sensory/tests/${fromSensoryId}`);
      const test = response.data;
      setSourceSensoryTest(test);
      
      // Pre-fill form data from sensory test
      setFormData(prev => ({
        ...prev,
        title: test.food_name || '',
        batch: test.batch || '',
        quantity: '1', // Default quantity, user should adjust
        source_sensory_test_id: fromSensoryId,
        custom_fields: {
          ...prev.custom_fields,
          'DMT (datum spotřeby)': test.dmt || '',
          'Datum výroby': test.production_date || '',
          'Den zkoušky': test.test_date || ''
        }
      }));
      
      toast.info('Data předvyplněna ze senzorické zkoušky');
    } catch (error) {
      toast.error('Chyba při načítání dat ze senzorické zkoušky');
    }
  };

  const fetchFormConfig = async () => {
    try {
      const [categoriesRes, statusesRes, fieldsRes, baseFieldsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/statuses'),
        api.get('/form-fields'),
        api.get('/base-fields')
      ]);

      setCategories(categoriesRes.data);
      setStatuses(statusesRes.data);
      setFormFields(fieldsRes.data);
      setBaseFields(baseFieldsRes.data);

      // Set default status (first one)
      if (statusesRes.data.length > 0) {
        setFormData(prev => ({ ...prev, status_id: statusesRes.data[0].id }));
      }
    } catch (error) {
      toast.error('Chyba při načítání formuláře');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.title || !formData.batch || !formData.quantity || !formData.status_id) {
        toast.error('Vyplňte všechna povinná pole');
        setLoading(false);
        return;
      }

      // Check custom required fields
      for (const field of formFields) {
        if (field.required && !formData.custom_fields[field.label]) {
          toast.error(`Pole "${field.label}" je povinné`);
          setLoading(false);
          return;
        }
      }

      const response = await api.post('/reports', {
        title: formData.title,
        batch: formData.batch,
        quantity: parseInt(formData.quantity),
        category_id: formData.category_id && formData.category_id !== 'none' ? formData.category_id : null,
        status_id: formData.status_id,
        custom_fields: formData.custom_fields,
        notification_emails: formData.notification_emails,
        dmt_notification: formData.dmt_notification,
        source_sensory_test_id: formData.source_sensory_test_id
      });

      toast.success('Hlášení vytvořeno');
      navigate(`/reports/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při vytváření hlášení');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomFieldChange = (label, value) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: {
        ...prev.custom_fields,
        [label]: value
      }
    }));
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Zadejte platnou emailovou adresu');
      return;
    }
    
    if (formData.notification_emails.includes(email)) {
      toast.error('Tento email už je přidán');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      notification_emails: [...prev.notification_emails, email]
    }));
    setEmailInput('');
  };

  const removeEmail = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      notification_emails: prev.notification_emails.filter(e => e !== emailToRemove)
    }));
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  // DMT Notification functions
  const addDmtEmail = () => {
    const email = dmtEmailInput.trim().toLowerCase();
    if (!email) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Zadejte platnou emailovou adresu');
      return;
    }
    
    if (dmtEmails.includes(email)) {
      toast.error('Tento email už je přidán');
      return;
    }
    
    setDmtEmails(prev => [...prev, email]);
    setDmtEmailInput('');
  };

  const removeDmtEmail = (emailToRemove) => {
    setDmtEmails(prev => prev.filter(e => e !== emailToRemove));
  };

  const handleDmtEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDmtEmail();
    }
  };

  const saveDmtNotification = () => {
    if (dmtEmails.length === 0) {
      toast.error('Přidejte alespoň jednu emailovou adresu');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      dmt_notification: {
        enabled: true,
        days_before: parseInt(dmtDaysBefore),
        emails: dmtEmails
      }
    }));
    
    setDmtModalOpen(false);
    toast.success(`DMT notifikace nastavena - ${dmtDaysBefore} dní před`);
  };

  const removeDmtNotification = () => {
    setFormData(prev => ({
      ...prev,
      dmt_notification: null
    }));
    setDmtEmails([]);
    setDmtDaysBefore('7');
    toast.info('DMT notifikace odstraněna');
  };

  const isDmtField = (label) => {
    const dmtLabels = ['dmt', 'datum spotřeby', 'datum minimální trvanlivosti', 'expiration', 'spotřeba'];
    return dmtLabels.some(l => label.toLowerCase().includes(l));
  };

  return (
    <Layout>
      <div className="p-8" data-testid="new-report-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/reports')}
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zpět
          </Button>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Source sensory test banner */}
          {sourceSensoryTest && (
            <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <FlaskConical className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-900">
                    Vytváříte hlášení ze senzorické zkoušky
                  </p>
                  <Link 
                    to={`/senzorika/${fromSensoryId}`}
                    className="text-sm text-purple-600 hover:text-purple-800 hover:underline"
                  >
                    {sourceSensoryTest.food_name} • {sourceSensoryTest.batch}
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-slate-900 mb-6">Nové hlášení</h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Default Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="sm:col-span-2">
                  <Label htmlFor="title">
                    Název <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Přesný název produktu"
                    required
                    data-testid="title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="batch">
                    Šarže <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="batch"
                    value={formData.batch}
                    onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                    placeholder="Číslo šarže"
                    required
                    data-testid="batch-input"
                  />
                </div>

                <div>
                  <Label htmlFor="quantity">
                    Počet kusů <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    placeholder="0"
                    required
                    min="1"
                    data-testid="quantity-input"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Kategorie</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                  >
                    <SelectTrigger id="category" data-testid="category-select">
                      <SelectValue placeholder="Vyberte kategorii" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Bez kategorie</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="status">
                    Stav <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.status_id}
                    onValueChange={(value) => setFormData({ ...formData, status_id: value })}
                  >
                    <SelectTrigger id="status" data-testid="status-select">
                      <SelectValue placeholder="Vyberte stav" />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(status => (
                        <SelectItem key={status.id} value={status.id}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: status.color }}
                            />
                            {status.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Custom Fields */}
              {formFields.length > 0 && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Další informace</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {formFields.map((field) => (
                      <div
                        key={field.id}
                        className={field.width === 'full' ? 'sm:col-span-2' : ''}
                      >
                        <Label htmlFor={`custom-${field.id}`}>
                          {field.label}
                          {field.required && <span className="text-red-500"> *</span>}
                        </Label>
                        {field.field_type === 'textarea' ? (
                          <Textarea
                            id={`custom-${field.id}`}
                            value={formData.custom_fields[field.label] || ''}
                            onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                            placeholder={field.placeholder || ''}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                          />
                        ) : field.field_type === 'select' ? (
                          <Select
                            value={formData.custom_fields[field.label] || ''}
                            onValueChange={(value) => handleCustomFieldChange(field.label, value)}
                          >
                            <SelectTrigger id={`custom-${field.id}`} data-testid={`custom-field-${field.id}`}>
                              <SelectValue placeholder={field.placeholder || 'Vyberte'} />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map(option => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : field.field_type === 'date' ? (
                          <div className="space-y-2">
                            <DatePicker
                              value={formData.custom_fields[field.label] || ''}
                              onChange={(value) => handleCustomFieldChange(field.label, value)}
                              placeholder={field.placeholder || 'Vyberte datum'}
                              data-testid={`custom-field-${field.id}`}
                            />
                            {isDmtField(field.label) && (
                              <div className="flex items-center gap-2">
                                {formData.dmt_notification ? (
                                  <div className="flex items-center gap-2 flex-1">
                                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                                      <Bell className="h-3 w-3 mr-1" />
                                      Notifikace {formData.dmt_notification.days_before} dní před
                                    </Badge>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={removeDmtNotification}
                                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setDmtModalOpen(true)}
                                    className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                    data-testid="dmt-notification-btn"
                                  >
                                    <Bell className="h-4 w-4 mr-1" />
                                    Notifikovat končící DMT
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Input
                            id={`custom-${field.id}`}
                            type={field.field_type}
                            value={formData.custom_fields[field.label] || ''}
                            onChange={(e) => handleCustomFieldChange(field.label, e.target.value)}
                            placeholder={field.placeholder || ''}
                            required={field.required}
                            data-testid={`custom-field-${field.id}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Email Notifications */}
              <div className="border-t border-slate-200 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="h-5 w-5 text-cyan-600" />
                  <h3 className="text-lg font-semibold text-slate-900">Email notifikace</h3>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Přidejte emailové adresy osob, které budou notifikovány při vytvoření a změnách tohoto hlášení.
                </p>
                
                <div className="flex gap-2 mb-3">
                  <Input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyPress={handleEmailKeyPress}
                    placeholder="email@example.com"
                    className="flex-1"
                    data-testid="notification-email-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addEmail}
                    className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    data-testid="add-email-button"
                  >
                    Přidat
                  </Button>
                </div>
                
                {formData.notification_emails.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="email-list">
                    {formData.notification_emails.map((email) => (
                      <Badge
                        key={email}
                        variant="secondary"
                        className="bg-cyan-50 text-cyan-700 hover:bg-cyan-100 pl-3 pr-1 py-1.5 flex items-center gap-1"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeEmail(email)}
                          className="ml-1 hover:bg-cyan-200 rounded-full p-0.5"
                          data-testid={`remove-email-${email}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/reports')}
                  className="flex-1"
                  data-testid="cancel-button"
                >
                  Zrušit
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  data-testid="submit-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Ukládání...' : 'Vytvořit hlášení'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* DMT Notification Modal */}
      <Dialog open={dmtModalOpen} onOpenChange={setDmtModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-500" />
              Nastavení DMT notifikace
            </DialogTitle>
            <DialogDescription>
              Nastavte kdy a komu se má odeslat upozornění na blížící se konec data minimální trvanlivosti.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="days-before" className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-slate-500" />
                Notifikovat před koncem DMT
              </Label>
              <Select value={dmtDaysBefore} onValueChange={setDmtDaysBefore}>
                <SelectTrigger id="days-before">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 den před</SelectItem>
                  <SelectItem value="3">3 dny před</SelectItem>
                  <SelectItem value="5">5 dní před</SelectItem>
                  <SelectItem value="7">7 dní před</SelectItem>
                  <SelectItem value="14">14 dní před</SelectItem>
                  <SelectItem value="30">30 dní před</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-slate-500" />
                Emailové adresy pro notifikaci
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  type="email"
                  value={dmtEmailInput}
                  onChange={(e) => setDmtEmailInput(e.target.value)}
                  onKeyPress={handleDmtEmailKeyPress}
                  placeholder="email@example.com"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addDmtEmail}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  Přidat
                </Button>
              </div>
              
              {dmtEmails.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {dmtEmails.map((email) => (
                    <Badge
                      key={email}
                      variant="secondary"
                      className="bg-amber-50 text-amber-700 hover:bg-amber-100 pl-3 pr-1 py-1.5 flex items-center gap-1"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeDmtEmail(email)}
                        className="ml-1 hover:bg-amber-200 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDmtModalOpen(false)}>
              Zrušit
            </Button>
            <Button 
              onClick={saveDmtNotification}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Bell className="h-4 w-4 mr-2" />
              Uložit notifikaci
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
